import React, { useMemo, type MutableRefObject } from 'react';
import { Table } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import useIntls from '@opm/hooks/useIntls';
import {
  useXHashtagMatrix,
  X_HASHTAG_DEFAULT_PAGE_INFO,
} from './hooks/useXHashtagMatrix';
import { getColumnHeader } from './transformMatrix';
import RankCell from './RankCell';
import type { IXHashtagApiFilter, IXHashtagMatrixRow } from './types';

const { groupable, scrollable } = Table.addons;

const MIN_HEIGHT = 360;

export interface IXHashtagMatrixTableProps {
  filter: IXHashtagApiFilter | null;
  onHasDataDate?: (date: string) => void;
  onHasDownloadChange?: (hasDownload: boolean) => void;
  downloadRef?: MutableRefObject<(() => void) | null>;
  isFirstRequestRef?: MutableRefObject<boolean>;
}

function HashtagCell({
  hashtag,
  trendingLabel,
}: {
  hashtag: string;
  trendingLabel: string;
}) {
  if (!hashtag) {
    return <span>-</span>;
  }
  return (
    <div className="dbom-x-hashtag-cell">
      <div className="dbom-x-hashtag-cell__title">{hashtag}</div>
      {trendingLabel ? (
        <div className="dbom-x-hashtag-cell__label">{trendingLabel}</div>
      ) : null}
    </div>
  );
}

export default function XHashtagMatrixTable({
  filter,
  onHasDataDate,
  onHasDownloadChange,
  downloadRef,
  isFirstRequestRef,
}: IXHashtagMatrixTableProps) {
  const { intl } = useIntls();
  const {
    matrixView,
    paginatedRows,
    rowTotal,
    total,
    loading,
    groupBy,
    pageInfo,
    changePageInfo,
  } = useXHashtagMatrix({
    filter,
    onHasDataDate,
    onHasDownloadChange,
    downloadRef,
    isFirstRequestRef,
  });

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'rank',
        header: intl('Rank'),
        width: 80,
        align: 'center' as const,
        fixed: 'left' as const,
        render: (row: IXHashtagMatrixRow) => <RankCell ranking={row.rank} />,
      },
    ];

    matrixView.columns.forEach((col) => {
      cols.push({
        key: col.key,
        header: getColumnHeader(col, groupBy, matrixView.isFlatHeader),
        width: 200,
        align: 'left' as const,
        render: (row: IXHashtagMatrixRow) => (
          <HashtagCell
            hashtag={String(row[col.key] || '')}
            trendingLabel={String(row[`${col.key}__label`] || '')}
          />
        ),
      });
    });

    return cols;
  }, [matrixView.columns, matrixView.isFlatHeader, groupBy, intl]);

  const tableAddons = useMemo(() => {
    const addons = [scrollable({})];
    if (!matrixView.isFlatHeader && matrixView.headerGroups.length > 0) {
      addons.unshift(
        groupable({
          headerGroups: matrixView.headerGroups.map((group) => ({
            key: group.key,
            title: group.title,
            align: 'center' as const,
            children: group.children,
          })),
        }),
      );
    }
    return addons;
  }, [matrixView.headerGroups, matrixView.isFlatHeader]);


  return (
    <CardCommon
      loading={loading}
      contentCard
      loadingHeight={MIN_HEIGHT}
      emptyTitleText={intl('No data under current filter selections')}
      hasEmptyDesc={false}
      isEmpty={!loading && total === 0}
      emptyHeight={MIN_HEIGHT}
    >
      <div className="dbom-x-hashtag-table">
        <TableCommonHook
          records={paginatedRows}
          recordKey={(row: IXHashtagMatrixRow) => String(row.rank)}
          columns={columns}
          bordered="all"
          addons={tableAddons}
          total={rowTotal}
          verticalTop={false}
          pageInfo={pageInfo}
          setPageInfo={changePageInfo}
          defaultPageInfo={X_HASHTAG_DEFAULT_PAGE_INFO}
          pageSizeOptions={[10, 20, 30]}
        />
      </div>
    </CardCommon>
  );
}
